import { t, TOptions } from "i18next";
import { IDropdownOption } from "../interfaces/IDropdownOptions";
import { SubscriptionLevel } from "../interfaces/SubscriptionLevel";
import { MessageInput, Plan } from "../graphql/schema";
import { AdditionalModule, BaseModuleItem, ProcessItem, OrderFormData, Product } from "../interfaces/OrderFormData";

export const enumValuesToOptions = <T extends {}>(
  enumObj: T
): IDropdownOption[] => {
  const options = [];
  for (const [key, value] of Object.entries(enumObj)) {
    if (isNaN(Number(value))) {
      options.push({ key: Number(key), text: t(String(value)) });
    }
  }
  return options;
};

export const translateEnumValue = <T extends {}>(
  enumObj: T,
  key: number,
  tOptions?: TOptions
): string => {
  const value = Object.entries(enumObj).find((x) => x[1] === key)?.[0];
  return value ? t(String(value)) : "";
};

export const mapTitleToSubscriptionLevel = (
  title: string
): SubscriptionLevel => {
  switch (title.toLowerCase()) {
    case "optimal":
      return SubscriptionLevel.OPTIMAL;
    case "stor":
      return SubscriptionLevel.STOR;
    case "grundläggande":
      return SubscriptionLevel.GRUNDLAGGANDE;
    case "minimal":
      return SubscriptionLevel.MINIMAL;
    case "startup":
      return SubscriptionLevel.STARTUP;
    default:
      return SubscriptionLevel.GRUNDLAGGANDE;
  }
};

export const getSubscriptionFromEnum = (
  subscriptions: Plan[],
  level: SubscriptionLevel
): Plan | undefined => {
  return subscriptions.find(
    (s) => mapTitleToSubscriptionLevel(s.title ?? "") === level
  );
};

export const calculateMonthlyCost = (
  products: Product[],
  additionalModules: AdditionalModule[],
  baseModules?: BaseModuleItem[],
  processes?: ProcessItem[]
) => {
  const toNumber = (x: any) => Number(x) || 0;

  const monthlyCost =
    products.reduce((sum, p) => sum + toNumber(p.monthlyCost), 0) +
    additionalModules.reduce((sum, a) => sum + toNumber(a.monthlyCost), 0) +
    (baseModules ?? []).reduce((sum, bm) => sum + toNumber(bm.monthlyCost), 0) +
    (processes ?? []).reduce((sum, k) => sum + toNumber(k.monthlyCost), 0);

  return formatPrice(monthlyCost);
};

export const formatPrice = (price: string | number) => {
  return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
};

export const sendOrderEmail = async (message: MessageInput): Promise<void> => {
  const response = await fetch(
    `${process.env.REACT_APP_CMS_URL}/api/messages`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${process.env.REACT_APP_CMS_JWT}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ data: message }),
    }
  );

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(text || `Failed to send order email (${response.status})`);
  }
};

export const formatOrderEmail = (data: OrderFormData): string => {
  const {
    subscription,
    numberOfUsers,
    numberOfEmployees,
    numberOfWorkplaces,
    organisation,
    invoice,
    contactDetails,
    products,
    additionalModules,
  } = data;

  const row = (label: string, value: string | number) =>
    `<tr>
      <td style="padding:6px 12px 6px 0;color:#666;font-size:14px;white-space:nowrap;vertical-align:top">${label}</td>
      <td style="padding:6px 0;font-size:14px;font-weight:600;color:#111;vertical-align:top">${value || "–"}</td>
    </tr>`;

  const section = (title: string, content: string) =>
    `<div style="margin-bottom:24px">
      <div style="background:#0d3b6e;color:#fff;padding:8px 16px;font-size:13px;font-weight:700;letter-spacing:0.5px;border-radius:4px 4px 0 0;text-transform:uppercase">${title}</div>
      <div style="border:1px solid #e0e0e0;border-top:none;padding:12px 16px;border-radius:0 0 4px 4px">
        ${content}
      </div>
    </div>`;

  const table = (rows: string) =>
    `<table style="width:100%;border-collapse:collapse">${rows}</table>`;

  const productsRows = products?.length
    ? products
        .map(
          (p) =>
            `<tr>
              <td style="padding:6px 0;font-size:14px;border-bottom:1px solid #f0f0f0">${p.name}</td>
              <td style="padding:6px 0;font-size:14px;border-bottom:1px solid #f0f0f0;text-align:center">${p.quantity}</td>
              <td style="padding:6px 0;font-size:14px;border-bottom:1px solid #f0f0f0;text-align:right;font-weight:600">${p.monthlyCost !== undefined ? `${formatPrice(p.monthlyCost)} kr/mån` : "–"}</td>
            </tr>`
        )
        .join("")
    : `<tr><td colspan="3" style="padding:8px 0;color:#999;font-size:14px">Inga produkter valda.</td></tr>`;

  const totalCost = (products ?? []).reduce((sum, p) => sum + (Number(p.monthlyCost) || 0), 0) +
    (additionalModules ?? []).reduce((sum, a) => sum + (Number(a.monthlyCost) || 0), 0);

  const addonsRows = additionalModules?.length
    ? additionalModules
        .map(
          (a) =>
            `<tr>
              <td style="padding:6px 0;font-size:14px;border-bottom:1px solid #f0f0f0">${a.name}</td>
              <td style="padding:6px 0;font-size:14px;border-bottom:1px solid #f0f0f0;text-align:right;font-weight:600">${formatPrice(a.monthlyCost ?? 0)} kr/mån</td>
            </tr>`
        )
        .join("")
    : `<tr><td colspan="2" style="padding:8px 0;color:#999;font-size:14px">Inga tillägg valda.</td></tr>`;

  const processesList = data.processes?.length
    ? `<ul style="margin:0;padding-left:20px">${data.processes.map((d) => `<li style="font-size:14px;padding:2px 0">${d.name}</li>`).join("")}</ul>`
    : `<p style="margin:0;color:#999;font-size:14px">Inga processer valda.</p>`;

  return `<!DOCTYPE html>
<html>
<body style="margin:0;padding:0;background:#f5f5f5;font-family:Arial,sans-serif">
  <div style="max-width:640px;margin:32px auto;background:#fff;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,0.08)">
    <div style="background:#0d3b6e;padding:24px 32px">
      <h1 style="margin:0;color:#fff;font-size:22px">Ny beställning från 365Robust</h1>
      <p style="margin:8px 0 0;color:#a0c4ff;font-size:14px">Inkommen via 365robust.se</p>
    </div>
    <div style="padding:24px 32px">

      ${section("Översikt", table(
        row("Paket", translateEnumValue(SubscriptionLevel, subscription)) +
        row("Antal användare", numberOfUsers) +
        row("Antal anställda", numberOfEmployees) +
        row("Antal arbetsställen", numberOfWorkplaces)
      ))}

      ${section("Organisation", table(
        row("Namn", organisation.organisationName) +
        row("Organisationsnummer", organisation.organisationNumber) +
        row("Typ", organisation.organisationType) +
        row("Antal anställda", organisation.employees) +
        row("SNI-koder", organisation.snis) +
        row("Antal arbetsställen", organisation.workplaces)
      ))}

      ${section("Fakturering", table(
        row("Fakturanamn", invoice.invoiceName) +
        row("Adress", invoice.invoiceAddress) +
        row("Postnummer / Ort", `${invoice.postalCode} ${invoice.county}`) +
        row("Referens", invoice.invoiceReference) +
        row("Telefon", invoice.phoneNumber) +
        row("E-post", invoice.email) +
        row("Övrigt", invoice.otherInvoiceDetails || "–")
      ))}

      ${section("Kontaktperson", table(
        row("Namn", `${contactDetails.firstName} ${contactDetails.lastName}`) +
        row("E-post", contactDetails.contactEmail) +
        row("Telefon", contactDetails.contactPhoneNumber) +
        row("Övrig information", contactDetails.otherInformation || "–")
      ))}

      ${section("Produkter &amp; pris",
        `<table style="width:100%;border-collapse:collapse">
          <thead>
            <tr style="border-bottom:2px solid #e0e0e0">
              <th style="padding:6px 0;font-size:13px;text-align:left;color:#666">Produkt</th>
              <th style="padding:6px 0;font-size:13px;text-align:center;color:#666">Antal</th>
              <th style="padding:6px 0;font-size:13px;text-align:right;color:#666">Kostnad</th>
            </tr>
          </thead>
          <tbody>${productsRows}</tbody>
          <tfoot>
            <tr>
              <td colspan="2" style="padding:10px 0 4px;font-size:14px;font-weight:700">Totalt per månad</td>
              <td style="padding:10px 0 4px;font-size:16px;font-weight:700;text-align:right;color:#0d3b6e">${formatPrice(totalCost)} kr</td>
            </tr>
          </tfoot>
        </table>`
      )}

      ${section("Processer", processesList)}

      ${section("Tillägg",
        `<table style="width:100%;border-collapse:collapse">
          <tbody>${addonsRows}</tbody>
        </table>`
      )}

    </div>
  </div>
</body>
</html>`;
};
